import React, { useState, useEffect, useRef } from 'react';
import { 
  Card, 
  Typography, 
  Form, 
  Input, 
  Select, 
  Switch, 
  Button, 
  notification, 
  Spin,
  Row,
  Col,
  Space,
  Upload,
  Image,
  Radio
} from 'antd';
import { 
  ArrowLeftOutlined, 
  SaveOutlined, 
  EyeOutlined, 
  UploadOutlined,
  LinkOutlined,
  PictureOutlined
} from '@ant-design/icons';
import { useNavigate, useParams } from 'react-router-dom';
import { newsService } from '../../services';
import apiClient from '../../services/api';
import RichTextEditor from '../../components/RichTextEditor';

const { Title } = Typography;
const { TextArea } = Input;
const { Option } = Select;

const NewsForm = () => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [topics, setTopics] = useState([]);
  const [categories, setCategories] = useState([]);
  const [isEditing, setIsEditing] = useState(false);
  const [contentHtml, setContentHtml] = useState('');
  const [showPreview, setShowPreview] = useState(false);
  const [imageMethod, setImageMethod] = useState('url'); // 'url' hoặc 'upload'
  const [uploadedImageUrl, setUploadedImageUrl] = useState('');
  const [uploading, setUploading] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const richTextEditorRef = useRef(null); // Thêm ref cho RichTextEditor
  
  const navigate = useNavigate();
  const { id } = useParams();

  // Mobile responsive detection
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    loadTopics();
    loadCategories();
    
    if (id) {
      setIsEditing(true);
      loadNewsDetail();
    }
  }, [id]);

  // Effect để set nội dung vào RichTextEditor khi contentHtml thay đổi
  useEffect(() => {
    if (contentHtml && richTextEditorRef.current && isEditing) {
      //console.log('🖊️ Setting content to RichTextEditor:', contentHtml.substring(0, 100) + '...');
      // Đợi một chút để đảm bảo component đã render xong
      const timer = setTimeout(() => {
        if (richTextEditorRef.current && richTextEditorRef.current.setContent) {
          richTextEditorRef.current.setContent(contentHtml);
        }
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [contentHtml, isEditing]);

  // Load danh sách chủ đề
  const loadTopics = async () => {
    try {
      const response = await newsService.getTopics();
      setTopics(response.data || []);
    } catch (error) {
      notification.error({
        message: 'Lỗi',
        description: 'Không thể tải danh sách chủ đề'
      });
    }
  };

  // Load danh sách danh mục
  const loadCategories = async () => {
    try {
      const response = await newsService.getCategories();
      setCategories(response.data || []);
    } catch (error) {
      notification.error({
        message: 'Lỗi',
        description: 'Không thể tải danh sách danh mục'
      });
    }
  };

  // Load chi tiết tin tức (khi edit)
  const loadNewsDetail = async () => {
    try {
      setLoading(true);
      //console.log('🔍 Loading news detail for ID:', id);
      
      const response = await newsService.getById(id);
      //console.log('📰 API Response:', JSON.stringify(response, null, 2));
      
      if (response.success && response.data) {
        // API trả về data là array, lấy phần tử đầu tiên
        let newsData;
        if (Array.isArray(response.data) && response.data.length > 0) {
          newsData = response.data[0];
        } else if (typeof response.data === 'object') {
          newsData = response.data;
        }
        
        if (newsData) {
          //console.log('📝 News data:', newsData);
          let htmlContent = newsData.content_html || '';
          
          // Nếu là HTML document đầy đủ, chỉ lấy nội dung trong body
          if (htmlContent.includes('<!doctype') || htmlContent.includes('<html')) {
            //console.log('🔍 Detected full HTML document, extracting body content...');
            const bodyMatch = htmlContent.match(/<main[^>]*>([\s\S]*?)<\/main>/i) || 
                             htmlContent.match(/<body[^>]*>([\s\S]*?)<\/body>/i);
            if (bodyMatch && bodyMatch[1]) {
              htmlContent = bodyMatch[1].trim();
              //console.log('✂️ Extracted content:', htmlContent.substring(0, 200) + '...');
            } else {
              // Fallback: remove HTML document tags but keep content
              htmlContent = htmlContent
                .replace(/<!doctype[^>]*>/gi, '')
                .replace(/<\/?html[^>]*>/gi, '')
                .replace(/<head[\s\S]*?<\/head>/gi, '')
                .replace(/<\/?body[^>]*>/gi, '')
                .trim();
              //console.log('🧹 Cleaned HTML:', htmlContent.substring(0, 200) + '...');
            }
          }
          
          setContentHtml(htmlContent);
          
          // Điền dữ liệu vào form
          form.setFieldsValue({
            title: newsData.title || '',
            summary: newsData.summary || '',
            content_html: htmlContent,
            cover_image: newsData.cover_image || '',
            topic_id: newsData.topic_id || undefined,
            category_id: newsData.category_id || undefined,
            is_featured: newsData.is_featured === 1 || newsData.is_featured === true,
            is_new: newsData.is_new === 1 || newsData.is_new === true,
          });
          
          // Set nội dung vào RichTextEditor bằng cách force re-render
          // Đặt một flag để đảm bảo component re-render với dữ liệu mới
          //console.log('✅ Form filled with data:');
          //console.log('- Title:', newsData.title);
          //console.log('- Summary:', newsData.summary);
          //console.log('- HTML Content length:', htmlContent.length);
          
          notification.success({
            message: 'Thành công',
            description: 'Đã tải thông tin tin tức'
          });
        } else {
          throw new Error('Không tìm thấy dữ liệu tin tức');
        }
      } else {
        throw new Error('Phản hồi API không hợp lệ');
      }
    } catch (error) {
      //console.error('❌ Error loading news detail:', error);
      notification.error({
        message: 'Lỗi',
        description: 'Không thể tải thông tin tin tức: ' + (error.message || 'Lỗi không xác định')
      });
      navigate('/news/list');
    } finally {
      setLoading(false);
    }
  };

  // Xử lý thay đổi nội dung HTML
  const handleContentChange = (content) => {
    //console.log('📝 Content changed:', content?.length, 'characters');
    setContentHtml(content);
    form.setFieldsValue({ content_html: content });
  };

  // Toggle preview
  const togglePreview = () => {
    setShowPreview(!showPreview);
  };

  // Handle image upload
  const handleImageUpload = async (file) => {
    setUploading(true);
    
    try {
      const formData = new FormData();
      formData.append('image', file);
      
      const response = await apiClient.post('/api/uploads/image', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      
      if (!response.data.success) {
        throw new Error(response.data.message || 'Upload failed');
      }
      
      const imageUrl = response.data.data.url;
      
      setUploadedImageUrl(imageUrl);
      form.setFieldsValue({ cover_image: imageUrl });
      
      notification.success({
        message: 'Thành công',
        description: 'Upload ảnh thành công!',
      });
      
    } catch (error) {
      //console.error('Upload error:', error);
      notification.error({
        message: 'Lỗi',
        description: error.message || 'Không thể upload ảnh. Vui lòng thử lại!',
      });
    } finally {
      setUploading(false);
    }
    
    return false; // Prevent auto upload
  };

  // Handle image method change
  const handleImageMethodChange = (e) => {
    setImageMethod(e.target.value);
    if (e.target.value === 'url') {
      setUploadedImageUrl('');
      form.setFieldsValue({ cover_image: '' });
    }
  };

  // Xử lý submit form
  const handleSubmit = async (values) => {
    try {
      setLoading(true);
      
      // Đảm bảo content_html được cập nhật từ editor
      const currentContentHtml = contentHtml || values.content_html || '';
      
      const submitData = {
        ...values,
        content_html: currentContentHtml,
        cover_image: values.cover_image || null,
        topic_id: values.topic_id || null,
        category_id: values.category_id || null,
        is_featured: values.is_featured ? 1 : 0,
        is_new: values.is_new ? 1 : 0,
      };

      //console.log('📤 Sending data to server:', submitData);

      if (isEditing) {
        await newsService.update(id, submitData);
        notification.success({
          message: 'Thành công',
          description: 'Cập nhật tin tức thành công'
        });
      } else {
        await newsService.create(submitData);
        notification.success({
          message: 'Thành công',
          description: 'Thêm tin tức thành công'
        });
      }
      
      navigate('/news/list');
    } catch (error) {
      //console.error('❌ Error submitting news:', error);
      notification.error({
        message: 'Lỗi',
        description: error.response?.data?.message || 'Có lỗi xảy ra khi lưu tin tức'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    navigate('/news/list');
  };

  if (loading && isEditing) {
    return (
      <div style={{ padding: isMobile ? 8 : 24, textAlign: 'center' }}>
        <Spin size="large" />
        <Title level={isMobile ? 5 : 4} style={{ marginTop: 16 }}>
          Đang tải...
        </Title>
      </div>
    );
  }

  return (
    <div style={{ padding: isMobile ? 8 : 24 }}>
      <style>
        {`
          .image-uploader .ant-upload {
            width: ${isMobile ? '120px' : '150px'};
            height: ${isMobile ? '120px' : '150px'};
            border: 1px dashed #d9d9d9;
            border-radius: 6px;
            display: flex;
            align-items: center;
            justify-content: center;
            cursor: pointer;
            transition: border-color 0.3s;
          }
          
          .image-uploader .ant-upload:hover {
            border-color: #1890ff;
          }
          
          .image-uploader .ant-upload-disabled {
            cursor: not-allowed;
            opacity: 0.6;
          }
          
          .news-content-preview {
            border: 1px solid #d9d9d9;
            border-radius: 6px;
            padding: ${isMobile ? '12px' : '16px'};
            min-height: ${isMobile ? '200px' : '300px'};
            background: #fff;
            font-size: ${isMobile ? '14px' : '16px'};
            word-break: break-word;
          }
          
          .news-content-preview img {
            max-width: 100%;
            height: auto;
          }
        `}
      </style>
      
      <Card>
        <div style={{ marginBottom: isMobile ? 16 : 24 }}>
          <Space size={isMobile ? 'small' : 'middle'} direction={isMobile ? 'vertical' : 'horizontal'} style={{ width: '100%' }}>
            <Button 
              icon={<ArrowLeftOutlined />} 
              onClick={handleCancel}
              size={isMobile ? 'middle' : 'default'}
            >
              Quay lại
            </Button>
            <Title level={isMobile ? 4 : 3} style={{ margin: 0 }}>
              {isEditing ? 'Chỉnh sửa tin tức' : 'Thêm tin tức mới'}
            </Title>
          </Space>
        </div>

        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          initialValues={{
            is_featured: false,
            is_new: false,
            content_html: '',
          }}
        >
          {isMobile ? (
            // Mobile Layout: Single column
            <div>
              {/* Main Content Fields */}
              <Form.Item
                name="title"
                label="Tiêu đề"
                rules={[
                  { required: true, message: 'Vui lòng nhập tiêu đề' },
                  { max: 255, message: 'Tiêu đề không được quá 255 ký tự' }
                ]}
              >
                <Input placeholder="Nhập tiêu đề tin tức" size="large" />
              </Form.Item>

              <Form.Item
                name="summary"
                label="Tóm tắt"
                rules={[
                  { required: true, message: 'Vui lòng nhập tóm tắt' },
                  { max: 500, message: 'Tóm tắt không được quá 500 ký tự' }
                ]}
              >
                <TextArea 
                  rows={3} 
                  placeholder="Nhập tóm tắt ngắn gọn về tin tức"
                  showCount
                  maxLength={500}
                />
              </Form.Item>

              {/* Mobile Settings Card */}
              <Card size="small" title="Cài đặt tin tức" style={{ marginBottom: 16 }}>
                <Row gutter={[8, 8]}>
                  <Col span={12}>
                    <Form.Item
                      name="topic_id"
                      label="Chủ đề"
                      style={{ marginBottom: 12 }}
                    >
                      <Select placeholder="Chọn chủ đề" allowClear size="small">
                        {topics.map(topic => (
                          <Option key={topic.id} value={topic.id}>
                            {topic.name}
                          </Option>
                        ))}
                      </Select>
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item
                      name="category_id"
                      label="Danh mục"
                      style={{ marginBottom: 12 }}
                    >
                      <Select placeholder="Chọn danh mục" allowClear size="small">
                        {categories.map(category => (
                          <Option key={category.id} value={category.id}>
                            {category.name}
                          </Option>
                        ))}
                      </Select>
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item
                      name="is_featured"
                      label="Tin nổi bật"
                      valuePropName="checked"
                      style={{ marginBottom: 12 }}
                    >
                      <Switch size="small" />
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item
                      name="is_new"
                      label="Tin mới"
                      valuePropName="checked"
                      style={{ marginBottom: 12 }}
                    >
                      <Switch size="small" />
                    </Form.Item>
                  </Col>
                </Row>
              </Card>

              {/* Image Upload */}
              <Form.Item
                label="Ảnh đại diện"
              >
                <Radio.Group 
                  value={imageMethod} 
                  onChange={handleImageMethodChange}
                  style={{ marginBottom: 16 }}
                  size="small"
                >
                  <Radio.Button value="url">
                    <LinkOutlined /> URL
                  </Radio.Button>
                  <Radio.Button value="upload">
                    <UploadOutlined /> Upload
                  </Radio.Button>
                </Radio.Group>

                {imageMethod === 'url' ? (
                  <Form.Item
                    name="cover_image"
                    style={{ marginBottom: 0 }}
                  >
                    <Input 
                      placeholder="https://example.com/image.jpg" 
                      prefix={<LinkOutlined />}
                      size="small"
                    />
                  </Form.Item>
                ) : (
                  <div>
                    <Upload
                      name="image"
                      listType="picture-card"
                      className="image-uploader"
                      showUploadList={false}
                      beforeUpload={handleImageUpload}
                      disabled={uploading}
                    >
                      {uploadedImageUrl ? (
                        <Image
                          src={uploadedImageUrl}
                          alt="Uploaded"
                          style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                          preview={true}
                        />
                      ) : (
                        <div>
                          {uploading ? (
                            <Spin />
                          ) : (
                            <>
                              <PictureOutlined style={{ fontSize: '20px', color: '#999' }} />
                              <div style={{ marginTop: 8, color: '#999', fontSize: '11px' }}>
                                Chọn ảnh
                              </div>
                            </>
                          )}
                        </div>
                      )}
                    </Upload>
                    
                    {uploadedImageUrl && (
                      <div style={{ marginTop: 8, fontSize: '10px', color: '#666', wordBreak: 'break-all' }}>
                        {uploadedImageUrl}
                      </div>
                    )}
                    
                    <Form.Item
                      name="cover_image"
                      style={{ display: 'none' }}
                    >
                      <Input />
                    </Form.Item>
                  </div>
                )}
              </Form.Item>

              {/* Content Editor */}
              <Form.Item
                name="content_html"
                label={
                  <Space size="small">
                    <span>Nội dung</span>
                    <Button 
                      type="link" 
                      size="small" 
                      icon={<EyeOutlined />}
                      onClick={togglePreview}
                    >
                      {showPreview ? 'Sửa' : 'Xem'}
                    </Button>
                  </Space>
                }
                rules={[
                  { required: true, message: 'Vui lòng nhập nội dung' }
                ]}
              >
                {showPreview ? (
                  <div 
                    className="news-content-preview"
                    dangerouslySetInnerHTML={{ __html: contentHtml }}
                  />
                ) : (
                  (!isEditing || !loading) ? (
                    <RichTextEditor
                      key={`rich-editor-${isEditing ? id : 'new'}-${contentHtml.length}`}
                      ref={richTextEditorRef}
                      value={contentHtml}
                      onChange={handleContentChange}
                      placeholder="Nhập nội dung tin tức..."
                      style={{ marginBottom: '16px' }}
                    />
                  ) : (
                    <div style={{ textAlign: 'center', padding: '30px' }}>
                      <Spin size="large" />
                      <div style={{ marginTop: 16, fontSize: '14px' }}>Đang tải trình soạn thảo...</div>
                    </div>
                  )
                )}
              </Form.Item>

              {/* Mobile Action Buttons */}
              <div style={{ marginTop: 24, padding: '16px 0' }}>
                <Space direction="vertical" style={{ width: '100%' }} size="middle">
                  <Button 
                    type="primary" 
                    htmlType="submit" 
                    loading={loading}
                    icon={<SaveOutlined />}
                    size="large"
                    block
                  >
                    {isEditing ? 'Cập nhật tin tức' : 'Thêm tin tức'}
                  </Button>
                  
                  <Button 
                    onClick={handleCancel}
                    size="large"
                    block
                  >
                    Hủy bỏ
                  </Button>
                </Space>
              </div>
            </div>
          ) : (
            // Desktop Layout: Two columns
            <Row gutter={24}>
            <Col span={16}>
              <Form.Item
                name="title"
                label="Tiêu đề"
                rules={[
                  { required: true, message: 'Vui lòng nhập tiêu đề' },
                  { max: 255, message: 'Tiêu đề không được quá 255 ký tự' }
                ]}
              >
                <Input placeholder="Nhập tiêu đề tin tức" size="large" />
              </Form.Item>

              <Form.Item
                name="summary"
                label="Tóm tắt"
                rules={[
                  { required: true, message: 'Vui lòng nhập tóm tắt' },
                  { max: 500, message: 'Tóm tắt không được quá 500 ký tự' }
                ]}
              >
                <TextArea 
                  rows={3} 
                  placeholder="Nhập tóm tắt ngắn gọn về tin tức"
                  showCount
                  maxLength={500}
                />
              </Form.Item>

              <Form.Item
                name="content_html"
                label={
                  <Space>
                    <span>Nội dung</span>
                    <Button 
                      type="link" 
                      size="small" 
                      icon={<EyeOutlined />}
                      onClick={togglePreview}
                    >
                      {showPreview ? 'Chỉnh sửa' : 'Xem trước'}
                    </Button>
                  </Space>
                }
                rules={[
                  { required: true, message: 'Vui lòng nhập nội dung' }
                ]}
              >
                {showPreview ? (
                  <div 
                    className="news-content-preview"
                    dangerouslySetInnerHTML={{ __html: contentHtml }}
                  />
                ) : (
                  // Chỉ render RichTextEditor sau khi đã load xong dữ liệu
                  (!isEditing || !loading) ? (
                    <RichTextEditor
                      key={`rich-editor-${isEditing ? id : 'new'}-${contentHtml.length}`}
                      ref={richTextEditorRef}
                      value={contentHtml}
                      onChange={handleContentChange}
                      placeholder="Nhập nội dung đầy đủ của tin tức. Bạn có thể định dạng văn bản, thêm hình ảnh, liên kết..."
                      style={{ marginBottom: '16px' }}
                    />
                  ) : (
                    <div style={{ textAlign: 'center', padding: '50px' }}>
                      <Spin size="large" />
                      <div style={{ marginTop: 16 }}>Đang tải trình soạn thảo...</div>
                    </div>
                  )
                )}
              </Form.Item>

              <Form.Item
                label="Ảnh đại diện"
              >
                <Radio.Group 
                  value={imageMethod} 
                  onChange={handleImageMethodChange}
                  style={{ marginBottom: 16 }}
                >
                  <Radio.Button value="url">
                    <LinkOutlined /> Nhập URL
                  </Radio.Button>
                  <Radio.Button value="upload">
                    <UploadOutlined /> Upload từ máy
                  </Radio.Button>
                </Radio.Group>

                {imageMethod === 'url' ? (
                  <Form.Item
                    name="cover_image"
                    style={{ marginBottom: 0 }}
                  >
                    <Input 
                      placeholder="https://example.com/image.jpg" 
                      prefix={<LinkOutlined />}
                    />
                  </Form.Item>
                ) : (
                  <div>
                    <Upload
                      name="image"
                      listType="picture-card"
                      className="image-uploader"
                      showUploadList={false}
                      beforeUpload={handleImageUpload}
                      disabled={uploading}
                    >
                      {uploadedImageUrl ? (
                        <Image
                          src={uploadedImageUrl}
                          alt="Uploaded"
                          style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                          preview={true}
                        />
                      ) : (
                        <div>
                          {uploading ? (
                            <Spin />
                          ) : (
                            <>
                              <PictureOutlined style={{ fontSize: '24px', color: '#999' }} />
                              <div style={{ marginTop: 8, color: '#999' }}>
                                Click để upload
                              </div>
                            </>
                          )}
                        </div>
                      )}
                    </Upload>
                    
                    {uploadedImageUrl && (
                      <div style={{ marginTop: 8, fontSize: '12px', color: '#666' }}>
                        URL: {uploadedImageUrl}
                      </div>
                    )}
                    
                    <Form.Item
                      name="cover_image"
                      style={{ display: 'none' }}
                    >
                      <Input />
                    </Form.Item>
                  </div>
                )}
              </Form.Item>
            </Col>

            <Col span={8}>
              <Card title="Cài đặt tin tức" size="small">
                <Form.Item
                  name="topic_id"
                  label="Chủ đề"
                >
                  <Select placeholder="Chọn chủ đề" allowClear>
                    {topics.map(topic => (
                      <Option key={topic.id} value={topic.id}>
                        {topic.name}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>

                <Form.Item
                  name="category_id"
                  label="Danh mục"
                >
                  <Select placeholder="Chọn danh mục" allowClear>
                    {categories.map(category => (
                      <Option key={category.id} value={category.id}>
                        {category.name}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>

                <Form.Item
                  name="is_featured"
                  label="Tin nổi bật"
                  valuePropName="checked"
                >
                  <Switch />
                </Form.Item>

                <Form.Item
                  name="is_new"
                  label="Tin mới"
                  valuePropName="checked"
                >
                  <Switch />
                </Form.Item>
              </Card>

              <Card title="Thao tác" size="small" style={{ marginTop: 16 }}>
                <Space direction="vertical" style={{ width: '100%' }}>
                  <Button 
                    type="primary" 
                    htmlType="submit" 
                    loading={loading}
                    icon={<SaveOutlined />}
                    size="large"
                    block
                  >
                    {isEditing ? 'Cập nhật tin tức' : 'Thêm tin tức'}
                  </Button>
                  
                  <Button 
                    onClick={handleCancel}
                    block
                  >
                    Hủy bỏ
                  </Button>
                </Space>
              </Card>
            </Col>
          </Row>
          )}
        </Form>
      </Card>
    </div>
  );
};

export default NewsForm;
